/**
 * English message catalog — the source of truth for the admin panel's keys.
 *
 * `tr.ts` is typed against this object, so a key added here fails the build
 * until Turkish has it too. That is deliberate: a missing translation that
 * silently falls back to English is invisible in review and is how half a
 * panel ends up bilingual.
 *
 * Keys are namespaced by the screen that owns them (`generate.*`), with
 * `common.*` for words that genuinely repeat everywhere. Placeholders are
 * `{name}` and are substituted by `t()`.
 */
export const en = {
  // ─── App shell ───────────────────────────────────────────────────────────
  'app.title': 'Prepzio Admin',
  'app.nav.toggle': 'Toggle navigation',
  'app.nav.authoring': 'Authoring',
  'app.nav.learners': 'Learners',
  'app.nav.productDocument': 'Product document',
  'app.nav.platform': 'Platform',
  'app.signOut': 'Sign out',
  'app.colorScheme.toggle': 'Toggle color scheme',
  'app.colorScheme.switchTo': 'Switch to {mode} mode',
  'app.colorScheme.light': 'light',
  'app.colorScheme.dark': 'dark',
  'app.language': 'Language',
  'app.language.change': 'Change language',

  // ─── Sign-in ─────────────────────────────────────────────────────────────
  'signIn.subtitle': 'Internal question-generation tooling. Admin access only.',
  'signIn.google': 'Sign in with Google',
  'signIn.or': 'or',
  'signIn.email': 'Email',
  'signIn.password': 'Password',
  'signIn.submit': 'Sign in with email',
  'signIn.failed': 'Sign-in failed',

  // ─── Model badge ─────────────────────────────────────────────────────────
  'model.withFallback': 'Generation model: {primary} (fallback: {fallback})',
  'model.noFallback':
    'Generation model: {primary}. No fallback provider is configured, so every tier runs on one provider with no second chance, and JD screenshot parsing (vision) is unavailable. Set the GEMINI_API_KEY worker secret to enable both.',
  'model.noFallbackShort': 'no fallback',

  // ─── Screen titles (also the nav labels) ─────────────────────────────────
  'screen.generate': 'Generate',
  'screen.questions': 'Question bank',
  'screen.itemModels': 'Item models',
  'screen.skills': 'Skills',
  'screen.skillQueue': 'Skill queue',
  'screen.reviewQueue': 'Review queue',
  'screen.bankHealth': 'Bank health',
  'screen.questionFlow': 'Question flow',
  'screen.launchReadiness': 'Launch readiness',
  'screen.users': 'Users',
  'screen.costs': 'Costs',
  'screen.businessRules': 'Business rules',
  'screen.userFlow': 'User flow',
  'screen.traceability': 'Traceability',
  'screen.health': 'System health',
  'screen.config': 'Config',
  'screen.simulate': 'Simulate',
  'screen.database': 'Database',
  'screen.emails': 'Emails',
  'screen.guide': 'Guide',
  'screen.review': 'Review',
  'screen.content': 'Content',
  'screen.roadmaps': 'Roadmaps',

  // ─── Common vocabulary ───────────────────────────────────────────────────
  'common.loading': 'Loading…',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.saved': 'Saved',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.refresh': 'Refresh',
  'common.retry': 'Retry',
  'common.search': 'Search',
  'common.filter': 'Filter',
  'common.all': 'All',
  'common.none': 'None',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.error': 'Error',
  'common.empty': 'Nothing here yet.',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.open': 'Open',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.apply': 'Apply',
  'common.reset': 'Reset',
  'common.skill': 'Skill',
  'common.level': 'Level',
  'common.status': 'Status',
  'common.count': 'Count',
  'common.total': 'Total',
  'common.actions': 'Actions',
  'common.details': 'Details',
  'common.unknown': 'Unknown',
  'common.never': 'Never',

  // ─── Error boundary ──────────────────────────────────────────────────────
  'errorBoundary.title': 'This screen crashed',
  'errorBoundary.retry': 'Try again',
  'errorBoundary.home': 'Back to the main screen',

  // ─── Seeded-content lock ─────────────────────────────────────────────────
  'seededLock.tooltip':
    '{what} is versioned in the repository — edit {file} and open a pull request. Decisions (approvals, the rule type, the freshness stamp) are still made here.',
  'seededLock.aria': '{what} is edited in {file}',

  // ─── Database ────────────────────────────────────────────────────────────
  'database.intro':
    'Raw SQL console on production D1 (open-source LibSQL/Outerbase Studio). Full read/write including UPDATE/DELETE/DDL — there is no undo, so double-check destructive queries.',
  'database.frameTitle': 'Database console',
  'database.queryFailed': 'Query failed',

  // ─── Roadmaps ────────────────────────────────────────────────────────────
  'roadmaps.intro':
    "Explore a roadmap's structure as a timeline: the 7-day plan's stations (main goal · practice · reflection), the practice sessions they spawned, and each session's ordered questions.",
  'roadmaps.select': 'Roadmap',
  'roadmaps.none': 'No roadmaps',
  'roadmaps.hideDiagram': 'Hide diagram',
  'roadmaps.showDiagram': 'View as diagram',
  'roadmaps.createdAt': 'created {date}',
  'roadmaps.deleteSim': 'Delete simulation',
  'roadmaps.deleteSimConfirm': 'Delete this simulation roadmap and its synthetic user?',
  'roadmaps.stations': '{count} stations',
  'roadmaps.sessions': '{count} sessions',
  'roadmaps.questionUses': '{count} question uses',

  // ─── Roadmap view ────────────────────────────────────────────────────────
  'roadmapView.empty': 'This roadmap has no stations or sessions yet.',
  'roadmapView.assessment': 'Assessment',
  'roadmapView.questionsInRoadmap': 'Questions in this roadmap',
  'roadmapView.assessmentHint':
    'The day stations below are the plan outline (no fixed questions until a learner practises). These are the baseline-assessment questions this roadmap was generated from — click any question to see its full detail (options, hints, rubric).',
  'roadmapView.day': 'Day {day}',
  'roadmapView.minutes': '~{minutes} min',
  'roadmapView.questionCount': '{count} questions',
  'roadmapView.questionCount_one': '1 question',

  // ─── Skill picker ────────────────────────────────────────────────────────
  'skillPicker.placeholder': 'Search skills…',
  'skillPicker.searching': 'Searching…',
  'skillPicker.noMatch': 'No skill matches',
  'skillPicker.hintFiltered': '{shown} of {total} matches — keep typing to narrow',
  'skillPicker.hintAll': '{shown} of {total} skills — type to search',

  // ─── Question inspector ──────────────────────────────────────────────────
  'question.detail': 'Question detail',
  'question.judge': 'judge {score}',
  'question.prompt': 'Prompt',
  'question.options': 'Options',
  'question.expectedConcepts': 'Expected concepts',
  'question.hintLadder': 'Hint ladder',
  'question.exampleStrongAnswer': 'Example strong answer',
  'question.commonMistakes': 'Common mistakes',
  'question.rubric': 'Rubric — {title}',
  'question.criterion': 'Criterion',
  'question.description': 'Description',
  'question.max': 'Max',
  'question.judgeRationale': 'Judge: {rationale}',
  'question.noQuestions': 'No questions.',
  'question.viewDetail': 'View full question detail',
  'question.confidence': 'confidence {value}/5',
  'question.answer': 'Answer',

  // ─── Authoring instruction ───────────────────────────────────────────────
  'authoring.title': 'Authoring instruction',
  'authoring.subtitle':
    'What the model is told about a good question. The form of the output — the hint ladder, the rubric, the JSON — is fixed in code and not editable here.',
  'authoring.hide': 'Hide',
  'authoring.editVersion': 'Edit (v{version})',
  'authoring.noneYet':
    'No instruction has been written yet. The built-in default is created the first time a generation runs — generate once, then come back to edit it.',
  'authoring.saveNote':
    'Saving creates v{version} and makes it active. Earlier versions are kept, so drafts already generated still point at the wording they came from.',
  'authoring.revert': 'Revert',
  'authoring.saveAs': 'Save as v{version}',

  // ─── Sparkline ───────────────────────────────────────────────────────────
  'sparkline.aria': '{label}: latest {last}, peak {peak}',
  'sparkline.title': '{label} — latest {last}, peak {peak}, {count} hourly points',
  'sparkline.noData': 'no data in this window',

  // ─── Domain enums ────────────────────────────────────────────────────────
  // Shared by every screen that renders a question's state. Kept in one place
  // so "draft" cannot end up translated three different ways.
  'enum.status.published': 'published',
  'enum.status.draft': 'draft',
  'enum.status.rejected': 'rejected',
  'enum.type.free_text': 'free text',
  'enum.type.mcq': 'multiple choice',
  'enum.type.coding': 'coding',
  'enum.difficulty.easy': 'easy',
  'enum.difficulty.medium': 'medium',
  'enum.difficulty.hard': 'hard',

  // ─── Content ─────────────────────────────────────────────────────────────
  'content.intro':
    'Everything in the question bank and its state. Published is live to learners; draft is awaiting review; rejected is discarded.',
  'content.bySkill': 'By skill',
  'content.totals': 'Totals:',
  'content.publishedCount': '{count} published',
  'content.draftCount': '{count} draft',
  'content.rejectedCount': '{count} rejected',
  'content.colSkill': 'Skill',
  'content.colPublished': 'Published',
  'content.colDraft': 'Draft',
  'content.colRejected': 'Rejected',
  'content.noSkills': 'No skills.',
  'content.browse': 'Browse',
  'content.allSkills': 'All skills',
  'content.type': 'Type',
  'content.difficulty': 'Difficulty',
  'content.nothingHere': 'Nothing here.',
  'content.nearDup': 'near-dup',
  'content.expected': 'Expected: {concepts}',
  'content.first50': 'Showing the first 50 — narrow the filters to see more.',

  // ─── Config ──────────────────────────────────────────────────────────────
  'config.intro':
    'Tunable domain rules and feature flags — edited here, read live by the API (no deploy). Each value falls back to its compiled-in default if cleared.',
  'config.infoTip':
    'These are the business rules that change. The Worker reads override ?? default from KV at runtime — a missing/invalid override always falls back to the code default, so config can never break correctness. Invariants (auth, uniqueness, workflow) stay enforced in code, not here.',
  'config.overridden': 'overridden',
  'config.default': 'default',
  'config.defaultValue': 'default: {value}',
  'config.on': 'on',
  'config.off': 'off',
  'config.unbounded': 'unbounded',
  'config.integer': 'integer',
  'config.invalidJson': 'Invalid JSON',

  // ─── Guide ───────────────────────────────────────────────────────────────
  // Static operator documentation. Prose rather than markup: the inline
  // emphasis the English original carried cannot survive translation without
  // fragmenting every sentence into three keys a translator cannot read.
  'guide.intro':
    'This tool turns the question bank from hand-written seeds into reviewed, LLM-generated content. The flow is always: generate → review → approve. Nothing you generate is shown to learners until you approve it.',

  'guide.access.title': '0 · Sign in & access',
  'guide.access.1': 'Sign in with the Google account on the admin allowlist.',
  'guide.access.2':
    "If you can sign in but every action returns “Admin access required”, your email is not on the API's ADMIN_EMAILS list — ask for it to be added.",

  'guide.templates.title': '1 · Prompt templates (do this first)',
  'guide.templates.intro':
    'Generation needs an active question_generation template. Open the Prompt templates tab:',
  'guide.templates.1':
    'Draft with AI: write a one-line brief (e.g. “senior backend, strict JSON, hard questions”) and press Draft with AI — the model expands it into a full template body in the editor, which you then edit and save.',
  'guide.templates.2':
    'Write the authoring instructions in the body and Save as new version. The first version is active automatically; later versions start inactive.',
  'guide.templates.3':
    'Activate the version you want generation to use. Templates are immutable — you never edit a version, you save a new one and activate it.',
  'guide.templates.4': 'Use Edit “name” to prefill the editor with the latest body and iterate.',

  'guide.generate.title': '2 · Generate drafts',
  'guide.generate.intro': 'On the Generate tab — this is the primary work:',
  'guide.generate.1':
    'Pick skill · type · difficulty · count (or leave the template as “Active default”). Press Generate.',
  'guide.generate.2':
    'Suggested — coverage gaps: the list shows which (skill × difficulty) cells are below the target of 5 published questions each, most-urgent first. Click Use to prefill the form for that cell.',
  'guide.generate.3':
    'The result panel reports token/cost and two gate outcomes: malformed-rejected (auto-discarded bad shape) and near-dup flagged (looks like an existing question). Everything else lands in Review as a draft.',
  'guide.generate.note':
    'Generation costs money and is rate/budget-limited. If you hit “Daily LLM budget reached”, try again later.',

  'guide.review.title': '3 · Review, edit & approve',
  'guide.review.intro':
    'The Review tab is the queue. It is sorted needs-attention first — near-duplicates, then unjudged, then the lowest quality score. Each card shows advisory badges:',
  'guide.review.1':
    "judge 0.xx — an automated quality score (0–1). ≥ 0.60 is “likely good” (green); below is worth a close look (amber). The judge's reasoning is shown under the badges.",
  'guide.review.2':
    'near-dup — the draft resembles an existing question; the matched question id is shown. Reject it unless it is genuinely distinct.',
  'guide.review.3':
    'malformed — auto-rejected for bad shape (visible under the rejected status filter).',
  'guide.review.advisory':
    'These signals are advisory — they never approve or reject for you. You decide. For each draft you can:',
  'guide.review.4':
    'Edit the prompt, concepts, 4-level hint ladder, example answer, mistakes, and rubric inline, then Save changes.',
  'guide.review.5':
    'Approve → publishes the question into the live bank (learners can now get it). This is the only step that makes content live.',
  'guide.review.6': 'Reject → discards the draft.',
  'guide.review.note': 'Use the status filter to review draft, published, or rejected items.',

  'guide.reset.title': '3b · Reset content (danger zone)',
  'guide.reset.intro':
    'The Reset tab bulk-deletes generated questions when you want to start a skill (or the whole draft store) over:',
  'guide.reset.1':
    'It only ever removes draft and rejected items. Published questions and anything a learner has already seen are never deletable here — that is enforced by the API, not just the UI.',
  'guide.reset.2':
    'Narrow the scope by status · skill · difficulty · type (all optional and combined), or leave everything at its default to clear all draft + rejected content.',
  'guide.reset.3':
    'Always Preview first — it shows the exact count that will be deleted. The Delete button stays disabled until you tick the confirm box, and asks once more before removing. Deletion is permanent.',

  'guide.content.title': "3c · Content (what's in the bank)",
  'guide.content.body':
    'The Content tab is a read-only overview of the whole question bank: a per-skill count matrix (published · draft · rejected) plus a filterable, browsable list by status · skill · type · difficulty. Use it to see what exists and in which state; edit/approve in Review, delete in Reset.',
  'guide.content.note':
    'The active generation model is always shown as a badge in the top-right header.',

  'guide.roadmaps.title': '3d · Roadmaps (relationship diagram)',
  'guide.roadmaps.body':
    "The Roadmaps tab visualizes a learner's 7-day plan as a diagram: pick a roadmap and see its stations (each day's main goal · practice · reflection), the practice sessions those stations spawned, and each session's ordered questions — so you can see exactly which questions were used where, and in what order. “View Mermaid source” shows the raw diagram definition.",

  'guide.goal.title': '4 · The goal (coverage matrix)',
  'guide.goal.body':
    'The target is every (skill × difficulty) cell at ≥ 5 published questions, with at least one MCQ per cell. Work the Suggested — coverage gaps list top-down; when it is empty, the v0 matrix is filled.',

  'guide.emails.title': '5 · Emails (transactional)',
  'guide.emails.1':
    'Plain mode: subject + message (optional HTML). Template mode: pick a published Resend template and fill its variables (required ones are marked *).',
  'guide.emails.2':
    'Every send uses an idempotency key so a double-click will not send twice. Press New key to start a fresh send.',
  'guide.emails.3':
    'Delivery history shows status (queued → delivered / bounced / …); filter by status or recipient and “Load more” to page.',

  'guide.trouble.title': 'Troubleshooting',
  'guide.trouble.1':
    '“No active question_generation template” → create + activate one in Prompt templates (step 1).',
  'guide.trouble.2':
    '“Daily LLM budget reached” → generation budget is spent for the day; retry later.',
  'guide.trouble.3': '“Admin access required” → your email is not on the allowlist.',
  'guide.trouble.4':
    'The model returned nothing valid → try again or refine the active prompt template.',

  // ─── Bank health ─────────────────────────────────────────────────────────
  'bankHealth.liveItems': 'Live items',
  'bankHealth.answersCollected': 'Answers collected',
  'bankHealth.medianAnswerTime': 'Median answer time',
  'bankHealth.needsLook': 'Needs a look',
  'bankHealth.needsLookHint':
    'Findings from real answers. Nothing here has been changed — the decision is yours.',
  'bankHealth.allHealthy':
    'No item has a finding against it. That is either a healthy bank or a young one: an item needs enough answers before anything can be said about it at all.',
  'bankHealth.colQuestion': 'Question',
  'bankHealth.colFindings': 'Findings',
  'bankHealth.colAnswers': 'Answers',
  'bankHealth.colExposure': 'Exposure',
  'bankHealth.mostAsked': 'Most asked',
  'bankHealth.mostAskedHint':
    'Share of every answer that went to each item. Not a defect list — this is where the same questions are reaching every learner, which the coverage report is the fix for.',
  'bankHealth.review': 'Review',
  'bankHealth.retire': 'Retire',
  'bankHealth.retireTitle': 'Retire this item',
  'bankHealth.sendForReview': 'Send for review',
  'bankHealth.measurementSays': 'What the measurement says',
  'bankHealth.retireNote':
    'Nothing is deleted. This question was answered by real learners and stays part of how they were measured — retiring it only stops it being asked again, and it can be brought back through review.',
  'bankHealth.why': 'Why',
  'bankHealth.whyHint':
    'Replaces the automated note on this item, so the next reader knows a person looked.',
  'bankHealth.severity.critical': 'critical',
  'bankHealth.severity.warning': 'warning',
  'bankHealth.lifecycle.published': 'published',
  'bankHealth.lifecycle.pretest': 'pretest',
  'bankHealth.lifecycle.under_review': 'under review',
  'bankHealth.lifecycle.retired': 'retired',

  // ─── Rejection reasons (shared: question flow, review queue) ─────────────
  'reason.factually_wrong': 'Factually wrong',
  'reason.ambiguous': 'Ambiguous',
  'reason.too_easy': 'Too easy',
  'reason.too_hard': 'Too hard',
  'reason.off_skill': 'Not about this skill',
  'reason.duplicate': 'Duplicate',
  'reason.badly_worded': 'Badly worded',
  'reason.biased': 'Biased or exclusionary',
  'reason.judge_blocked': 'Stopped by the judge',
  'reason.other': 'Other',

  // ─── Question flow ───────────────────────────────────────────────────────
  'flow.intro':
    'The line a question travels from the moment it is born to the moment it is retired: how the cell to fill is chosen, what generation draws on, which gates discard, where a human decides, when difficulty is measured. Every number here is read from the bank as it stands today — none of it is written by hand.',
  'flow.loadFailed': 'Could not read the question pipeline',
  'flow.published': 'Published:',
  'flow.generated': 'Generated:',
  'flow.rejected': 'Discarded:',
  'flow.waiting': '{count} waiting',
  'flow.discardedCount': '{count} discarded',
  'flow.discardedNode': 'Discarded',
  'flow.bottleneck': 'Bottleneck',
  'flow.bottleneckBody': 'Most items have piled up at {step}: {count} waiting. {where}',
  'flow.bottleneckWhere': 'The decision is made on that screen.',
  'flow.noBottleneck': 'No step has anything waiting.',
  'flow.section1': '01 · The line',
  'flow.section1Hint':
    'Dashed arrows are items leaving the line: three steps can discard an item, the rest only move it along.',
  'flow.section2': '02 · Steps',
  'flow.section2Hint':
    "Each step's own numbers and the screen where its decision is made. The ordering step has nothing waiting — its queue is every cell of the catalog that has not been filled yet.",
  'flow.section3': '03 · What was discarded, and where',
  'flow.section3Hint':
    'A single rejection means nothing; a reason that repeats is an instruction to change the prompt template.',
  'flow.noRejections': 'Nothing has been discarded yet.',
  'flow.colReason': 'Reason',
  'flow.colStep': 'Discarding step',
  'flow.colCount': 'Count',
  'flow.badlyWordedNote':
    "“Badly worded” can come from two sources and the two cannot be separated: the shape gate rejects a malformed generation for this reason, and the same reason is on the reviewer's closed list. Here it is attributed to the shape gate, while /review-stats counts it on the human-rejection side. The two screens share the definition, not the reason, so they do not describe the same bank differently.",
  'flow.decidesNothing': 'This screen decides nothing',
  'flow.decidesNothingBody':
    "Approval, rejection and retirement stay on their own screens — an item's state cannot be changed here. Nor is this the product claim User flow renders: there the product owner declares “ready / todo / gap”; here it is the bank as measured.",
  'flow.goToScreen': 'Go to screen',
  'flow.noScreen': 'No screen of its own — this step is managed from the repository.',

  // ─── Review ──────────────────────────────────────────────────────────────
  'review.judgeTitle': 'Advisory best-of-3 authoring judge score (PRE-171)',
  'review.nearDupTitle': 'Flagged as a near-duplicate (PRE-169)',
  'review.malformed': 'malformed',
  'review.malformedTitle': 'Auto-rejected by the golden-shape gate (PRE-170)',
  'review.nearDupNote': '⚠ Near-duplicate of an existing question ({id}).',
  'review.conceptsLabel': 'Expected concepts (comma-separated)',
  'review.mistakesLabel': 'Common mistakes (comma-separated)',
  'review.hintLadderLabel': 'Hint ladder (1 → 4)',
  'review.hintPlaceholder': 'Hint {n}',
  'review.rubricHeading': 'Rubric',
  'review.rubricTitlePlaceholder': 'Rubric title',
  'review.criterionLabel': 'Label',
  'review.saveChanges': 'Save changes',
  'review.approve': 'Approve',
  'review.reject': 'Reject',
  'review.savedNotice': 'Saved.',

  // ─── Question bank ───────────────────────────────────────────────────────
  'bank.infoTip':
    'published is the live bank learners draw from; draft awaits review; rejected was declined and kept. Click a prompt to see its full detail — options, hints, rubric.',
  'bank.range': '{from}–{to} of {total}',
  'bank.discardThese': 'Discard these {count}',
  'bank.discardTitle': 'Discard generated questions',
  'bank.discardBody':
    'This permanently deletes the {status} questions matching the current filters{unfiltered}.',
  'bank.discardUnfiltered': ' — every one of them, unfiltered',
  'bank.willGo': '{count} questions will go.',
  'bank.willGo_one': '1 question will go.',
  'bank.counting': 'Counting…',
  'bank.publishedSafe':
    'Published questions are never touched: a learner may have been measured on one, and deleting the row would rewrite that. Retire those instead.',
  'bank.discard': 'Discard',
  'bank.noMatch': 'No questions match these filters.',

  // ─── Generate ────────────────────────────────────────────────────────────
  'generate.forSkill': 'Generate for a skill',
  'generate.forSkillTip':
    'Fills every cell of this skill that is under its target — all three difficulties, at least one MCQ each. The target is {floor} for a skill nobody has asked for and grows with demand, so it is not the same number for two skills. How many to write is the shortfall, not a number you type. Work is queued: drafts arrive in the review queue as they are generated.',
  'generate.forSkillHint':
    'Pick a skill. Difficulty, type and count come from its coverage target.',
  'generate.run': 'Generate',
  'generate.missing': '{count} questions missing across {cells} cells.',
  'generate.missing_one': '{count} question missing across {cells} cells.',
  'generate.queued': 'Queued {jobs} generation jobs across {cells} cells',
  'generate.queued_one': 'Queued {jobs} generation job across {cells} cells',
  'generate.nothingToQueue': 'Nothing to queue — this skill is already at its target',
  'generate.cellLine': '{difficulty} · {deficit} missing · {steps}',
  'generate.draftsArrive':
    'Drafts appear in the review queue as they are generated — they are never shown to learners before a person approves them.',

  'generate.bulkTitle': 'Fill the most-wanted cells',
  'generate.bulkTip':
    'Takes the top of the same ranking and fills those cells, so you do not have to walk the list one skill at a time. Preview first — it costs nothing and says exactly how many drafts will be generated.',
  'generate.bulkSummary': '{cells} cells under target, {deficit} questions missing in all.',
  'generate.bulkSummary_one': '{cells} cell under target, {deficit} questions missing in all.',
  'generate.cellsToFill': 'Cells to fill',
  'generate.cellsToFillHint': 'Highest-ranked first',
  'generate.preview': 'Preview',
  'generate.queue': 'Queue',
  'generate.queueJobs': 'Queue {count} jobs',
  'generate.queueJobs_one': 'Queue {count} job',
  'generate.previewLine':
    '{cells} cells · {drafts} drafts to generate. Nothing has been queued yet.',
  'generate.previewLine_one':
    '{cells} cell · {drafts} drafts to generate. Nothing has been queued yet.',
  'generate.previewCell': '{skill} · {difficulty} · {deficit} missing',
  'generate.previewMore': '…and {count} more.',
  'generate.queuedBulk':
    'Queued {jobs} jobs across {cells} cells. Drafts arrive in the review queue as they are generated.',
  'generate.queuedBulk_one':
    'Queued {jobs} job across {cells} cells. Drafts arrive in the review queue as they are generated.',

  'generate.thinnest': 'Where the bank is thinnest',
  'generate.thinnestTip':
    'Cells under their target, ranked by deficit × demand: demand is how many learners claim the skill plus how many roadmaps target it, and prevalence only breaks ties. The same demand also raises the target — a wanted skill needs a deeper bank, because five items in a cell means everyone sees the same five. Click a row to select that skill.',
  'generate.legend':
    'have = published questions in this cell · target = how many it should hold, {floor} plus what demand bought · need = the shortfall · demand = learners claiming it plus roadmaps targeting it.',
  'generate.cellCounts': 'have {published} / {target} · need {deficit}{demand}',
  'generate.demandSuffix': ' · demand {demand}',
  'generate.select': 'Select',

  // ─── Review queue ────────────────────────────────────────────────────────
  'queue.waiting': 'Waiting for review',
  'queue.approvalRate': 'Approved of those a person saw',
  'queue.approvalRateHint': 'Low: judge too lenient. Very high: too strict.',
  'queue.stoppedBefore': 'Stopped before review',
  'queue.stoppedBeforeHint': 'Rejected, never deleted — reviewable.',
  'queue.empty': 'Nothing is waiting for review.',
  'queue.position': '{index} of {total}',
  'queue.judgePercent': 'judge {percent}%',
  'queue.nearDuplicate': 'near-duplicate',
  'queue.strongAnswer': 'Strong answer: {answer}',
  'queue.judgeSaid': 'What the judge said',
  'queue.notePlaceholder': 'Optional note — for what the reasons below do not cover',
  'queue.approve': 'Approve',
  'queue.keyHelp':
    'a approve · 1–9 reject with a reason · u step back. Nothing is sent until you apply.',
  'queue.end': 'End of the queue — apply your decisions.',
  'queue.apply': 'Apply {count} decisions',
  'queue.apply_one': 'Apply {count} decision',
  'queue.discard': 'Discard',
  'queue.skipped': '{count} did not apply',
  'queue.reasonsTitle': 'Why drafts get turned down',
  'queue.reasonsHint':
    'A reason that keeps repeating is an instruction to change the prompt template — and a labelled failure, which is what an eval case is made of.',

  // ─── Launch readiness ────────────────────────────────────────────────────
  'launch.title': 'Are we fit to launch',
  'launch.state.pass': 'pass',
  'launch.state.blocked': 'blocked',
  'launch.state.waived': 'waived',
  'launch.state.info': 'info',
  'launch.noBlockers': 'No gate is blocking.',
  'launch.blockers': '{count} gates are blocking: {names}.',
  'launch.readyNote':
    'Gates with a waiver written against them appear below — that is not a pass, it means they were left open deliberately.',
  'launch.notReadyNote': 'The worklist below says line by line why that is.',
  'launch.expiredTitle': 'Expired waiver',
  'launch.expiredBody':
    'These gates were once left open deliberately and the waiver has expired. The gate is blocking again.',
  'launch.expiredLine': '{key} · {owner} · expired on {date}',
  'launch.scorecard': 'Scorecard',
  'launch.scorecardHint':
    'The target and the current value per dimension. All of it is computed from data; the only thing a person can change is a waiver.',
  'launch.waiverLine': 'Waiver: {reason} — {owner}, until {date}',
  'launch.writeWaiver': 'Write a waiver',
  'launch.worklist': 'Worklist',
  'launch.worklistHint':
    'Derived from the scorecard, not ticked. A row closes by itself the moment its work is done; if an item is retired tomorrow it reopens.',
  'launch.noWork': 'No open work.',
  'launch.colTodo': 'To do',
  'launch.colDimension': 'Dimension',
  'launch.colDeficit': 'Missing',
  'launch.colScreen': 'Screen',
  'launch.openWaivers': 'Open waivers',
  'launch.openWaiversHint': 'Gates left open deliberately. Revoking one is also a decision.',
  'launch.until': '{owner} · until {date}',
  'launch.revoke': 'Revoke',
  'launch.waiverModalTitle': 'Waiver: {title}',
  'launch.waiverIntro':
    'You are deciding to ship with this gate open. The record is permanent: when it expires the gate blocks again, and an expired waiver stays visible in the list.',
  'launch.reason': 'Reason',
  'launch.reasonHint':
    'Somebody reading this in six months must understand why we decided this way.',
  'launch.owner': 'Owner',
  'launch.ownerHint': 'The person standing behind this decision.',
  'launch.expiresAt': 'Expires',
  'launch.expiresAtHint': 'YYYY-MM-DD. After this date the gate blocks again.',
  'launch.submitWaiver': 'Write the waiver',

  // ─── Skills ──────────────────────────────────────────────────────────────
  'skills.intro':
    'Skills group questions by subject (used by the generator, coverage gaps and roadmaps). A skill can only be deleted once it has no questions — deleting it would otherwise cascade-delete its whole bank.',
  'skills.new': 'New skill',
  'skills.searchPlaceholder': 'Title or slug — e.g. kafka, postgres, react',
  'skills.originAll': 'All',
  'skills.originCurated': 'Authored',
  'skills.originCatalog': 'Catalog',
  'skills.originFromCvs': 'From CVs',
  'skills.badge.curated': 'authored',
  'skills.badge.stackoverflow': 'catalog',
  'skills.badge.user_generated': 'from a CV',
  'skills.noMatch': 'No skill matches.',
  'skills.showing': 'Showing {shown} of {total}.',
  'skills.colTitle': 'Title',
  'skills.colSlug': 'Slug',
  'skills.colOrigin': 'Origin',
  'skills.colParent': 'Parent',
  'skills.colQuestions': 'Questions',
  'skills.root': 'root',
  'skills.deleteBlocked': 'Has {count} question(s) — move or delete them first',
  'skills.deleteConfirm': 'Delete skill “{title}”? This cannot be undone.',
  'skills.none': 'No skills yet.',
  'skills.editTitle': 'Edit skill',
  'skills.newTitle': 'New skill',
  'skills.title': 'Title',
  'skills.slug': 'Slug',
  'skills.slugHint': 'Lowercase words joined by hyphens. Unique across skills.',
  'skills.parent': 'Parent skill',
  'skills.parentHint': 'Leave empty for a top-level skill.',
  'skills.parentPlaceholder': 'No parent (root)',
  'skills.description': 'Description',
  'skills.create': 'Create',

  // ─── Item models ─────────────────────────────────────────────────────────
  'itemModels.writeTitle': 'Write an item model',
  'itemModels.writeTip':
    'A model is a question with holes in it. Slots say what each hole may hold; constraints say which combinations make sense. Preview is free and calls nothing — iterate there. Generating is the last step.',
  'itemModels.writeHint':
    'You write the stem and what may vary. The generator fills the holes and writes only the hints, the strong answer and the rubric — never the question itself.',
  'itemModels.title': 'Title',
  'itemModels.titlePlaceholder': 'Missing await',
  'itemModels.stem': 'Stem',
  'itemModels.stemHint': 'Use {SLOT} for each hole. UPPER_SNAKE names.',
  'itemModels.stemPlaceholder':
    'A developer logs a value right after calling {API}() and sees {SYMPTOM}. Why, and how would you fix it?',
  'itemModels.slots': 'Slots',
  'itemModels.slotsHint': 'What each hole may hold',
  'itemModels.constraints': 'Constraints',
  'itemModels.constraintsHint': 'Which combinations are coherent. [] when the slots vary freely.',
  'itemModels.concepts': 'What a correct answer must show',
  'itemModels.conceptsHint': 'One per line. This is the cognitive model in a few words.',
  'itemModels.previewFree': 'Preview — free',
  'itemModels.saveActive': 'Save as active',
  'itemModels.badJson': 'Slots or constraints are not valid JSON: {message}',
  'itemModels.fixFirst': 'Fix these first',
  'itemModels.yield': '{count} coherent items from this model',
  'itemModels.yield_one': '{count} coherent item from this model',
  'itemModels.andMore': '…and {count} more, in this order.',
  'itemModels.listTitle': 'Models',
  'itemModels.listTip':
    'yield is how many coherent items the model can produce; produced is how many drafts it actually has. A model written and never run is the same as no model.',
  'itemModels.none': 'No models yet. The form above writes the first one.',
  'itemModels.status.draft': 'draft',
  'itemModels.status.active': 'active',
  'itemModels.summary': 'yield {yield} · produced {produced} · {slots}{constraints}',
  'itemModels.noSlots': 'no slots',
  'itemModels.constraintCount': ' · {count} constraint(s)',
  'itemModels.items': 'Items',
  'itemModels.generate': 'Generate',
  'itemModels.written':
    '{count} drafts written from {attempted} stems{extra}. They are in the review queue — nothing reaches a learner until a person approves it.',
  'itemModels.written_one':
    '{count} draft written from {attempted} stems{extra}. They are in the review queue — nothing reaches a learner until a person approves it.',
  'itemModels.failedSuffix': ' · {count} could not be completed',
  'itemModels.dupSuffix': ' · {count} near-duplicate',
  'itemModels.malformedSuffix': ' · {count} rejected by the gates',

  // ─── Skill queue ─────────────────────────────────────────────────────────
  'skillQueue.mergeTitle': '“{title}” — merge',
  'skillQueue.mergeTarget': 'Target skill',
  'skillQueue.mergeTargetPlaceholder': 'Search and pick',
  'skillQueue.mergeExplain':
    'The “{source}” row will NOT be deleted — it becomes a redirect to {target}.',
  'skillQueue.mergeDetail':
    'The claims of the {users} learners who named this skill on their CV, and the {questions} questions bound to it, stay where they are; a read follows the chain and arrives at {target}. From now on a CV writing the same name binds straight to {target}.',
  'skillQueue.merge': 'Merge',
  'skillQueue.promoteTitle': '“{title}” — take into the taxonomy',
  'skillQueue.heading': 'Title',
  'skillQueue.slug': 'Slug',
  'skillQueue.slugHint': 'Lowercase letters, digits and hyphens. Must be unique.',
  'skillQueue.parent': 'Parent skill',
  'skillQueue.parentPlaceholder': 'Root level',
  'skillQueue.promoteNote':
    'The row becomes curated — from now on it is a taxonomy entry we wrote.',
  'skillQueue.promote': 'Take into the taxonomy',
  'skillQueue.emptyTitle': 'The queue is empty — the taxonomy is current.',
  'skillQueue.emptyBody':
    'Every skill arriving from a CV either matched our catalog or was decided on. A new name will show up here; in the meantime no learner waits, because the row is already in use.',
  'skillQueue.banner':
    'These rows are IN USE right now — they were written to the catalog the moment they arrived from a CV and no learner waited anywhere (BR-90). The decision here is never “should this exist” but “where does it belong”.',
  'skillQueue.count': '{count} skills awaiting a decision · most common first',
  'skillQueue.colLabel': 'Label',
  'skillQueue.colUsage': 'Usage',
  'skillQueue.colQuestions': 'Questions',
  'skillQueue.colArrived': 'Arrived',
  'skillQueue.colDecision': 'Decision',
  'skillQueue.people': '{count} people',
  'skillQueue.accept': 'Accept',
  // ─── Business rules ──────────────────────────────────────────────────────
  'br.type.product': 'product',
  'br.type.productHint': 'A product decision a learner can feel.',
  'br.type.platform': 'platform',
  'br.type.platformHint': 'How the system carries a promise: envelopes, idempotency, telemetry.',
  'br.config.migrated': '⚙️ tunable',
  'br.config.migratedHint':
    'A preference: another value would also have been defensible. Changed from the Config screen, without a deploy.',
  'br.config.hybrid': '⚙️ partly tunable',
  'br.config.hybridHint':
    'Part of the rule is tunable and part is fixed in code — the note says which is which.',
  'br.config.in-code': 'in code',
  'br.config.in-codeHint':
    "Not a preference but the algorithm's definition. Another value would claim something never measured, so it is not a knob.",
  'br.config.invariant': '🔒 invariant',
  'br.config.invariantHint':
    'A correctness rule — identity, uniqueness, workflow. If it were tunable it could be set wrong, so it is enforced in code.',
  'br.config.unknown': '—',
  'br.config.unknownHint': "This rule's tunability has not been classified yet.",
  'br.intro':
    "Prepzio's product/domain invariants (BR-*), grouped by process. Search by id, rule text, or source; filter by process. PRE-125/126: the rule text and its type are authored in the repository and applied on deploy. What is made here is the review — one person saying they have read a rule and it is right. Rewriting the sentence makes that review stale, because it was given to the words that changed.",
  'br.infoTip':
    'Each rule shows its enforcing source (the file:line reverse-link of the // BR-n comments in code) and its runtime-config posture: ⚙️ tunable (admin-editable via Config), partly tunable, in code, or 🔒 invariant. Ids are never reused — a deleted number stays retired, because code comments and blueprint anchors still name it.',
  'br.loadFailed': 'Could not load the rule catalog',
  'br.searchPlaceholder': 'id, rule text, or source…',
  'br.type': 'Type',
  'br.allTypes': 'All types',
  'br.process': 'Process',
  'br.allProcesses': 'All processes',
  'br.priority': 'Priority',
  'br.review': 'Review',
  'br.reviewedByMe': 'What I reviewed',
  'br.awaitingReading': 'Awaiting a read',
  'br.rejectedByMe': 'What I rejected',
  'br.addingRule': 'Adding a rule',
  'br.ruleText': 'The rule text',
  'br.summary': '{shown} of {total} rules · {product} product · {platform} platform',
  'br.awaitingReview': '{count} awaiting your review',
  'br.rejectedCount': '{count} rejected',
  'br.noMatch': 'No rules match your search.',
  'br.colDecision': 'Decision',
  'br.colRule': 'Rule',
  'br.colConfig': 'Config',
  'br.rejected': 'Rejected',
  'br.iReviewedIt': 'I have reviewed it',
  'br.textChanged': 'The text changed — it must be read again',
  'br.undoDecision': 'Undo the decision',
  'br.reject': 'Reject',
  'br.rejectTitle': '{id} — reject',
  'br.rejectReason': 'Reason',
  'br.rejectReasonHint':
    'Required. Rejecting is a claim that this sentence in the catalog does not describe the product correctly — somebody reading this row in a month will find the why here.',
  'br.reasonRequired': 'A reason is required',

  // ─── Traceability ────────────────────────────────────────────────────────
  'rtm.status.ready': 'ready',
  'rtm.status.todo': 'todo',
  'rtm.status.gap': 'gap',
  'rtm.kind.step': 'Flow step',
  'rtm.kind.backlog': 'Backlog',
  'rtm.kind.rule': 'Product rule',
  'rtm.verdict.covered': 'Covered',
  'rtm.verdict.partial': 'Partial',
  'rtm.verdict.not_covered': 'Not covered',
  'rtm.verdict.not_applicable': 'Out of scope',
  'rtm.all': 'All',
  'rtm.intro':
    'Every requirement here under a unique id: flow steps (F…), backlog items (B-…) and product rules (R-…). Each row shows which business rule (BR-…), which source file and which test it is bound to. Code anchors and the code review are derived and cannot be edited by hand; the only decision made by hand is the coverage verdict — did this capability actually land.',
  'rtm.apiUnreachable':
    'The API could not be reached — the matrix is shown from the document, coverage decisions did not load.',
  'rtm.stale':
    'This document was last compared against the code on {date} ({days} days ago, threshold {threshold} days). It should be reviewed again.',
  'rtm.traceability': 'Traceability',
  'rtm.tracedHint': '{traced} / {total} requirements bound to code',
  'rtm.decided': 'Decided',
  'rtm.decidedHint': '{covered} covered · {partial} partial · {notCovered} no',
  'rtm.decidedFallback': 'coverage decisions come from the API',
  'rtm.untraced': 'Untraced',
  'rtm.untracedHint': 'neither a BR nor a code anchor',
  'rtm.referencedBr': 'Referenced BRs',
  'rtm.referencedBrHint': 'verified against the business-rule catalog',
  'rtm.statusSpread': 'Status spread',
  'rtm.searchPlaceholder': 'Search: id, text, BR-72, roadmap.ts …',
  'rtm.untracedOnly': 'Untraced only',
  'rtm.allDecisions': 'All decisions',
  'rtm.myApprovals': 'What I approved',
  'rtm.awaitingMe': 'Awaiting my decision',
  'rtm.rowCount': '{shown} / {total} rows',
  'rtm.colId': 'ID',
  'rtm.colKind': 'Kind',
  'rtm.colRequirement': 'Requirement',
  'rtm.colBusinessRule': 'Business rule',
  'rtm.colCodeTest': 'Code / test',
  'rtm.colDecision': 'Coverage decision',
  'rtm.noBr': 'no BR',
  'rtm.decide': 'Decide',
  'rtm.decisionTitle': '{id} — coverage decision',
  'rtm.reason': 'Reason',
  'rtm.reasonHint':
    'Required for every verdict other than “Covered” — somebody reading this row in a month will find the why here.',
  'rtm.reasonRequired': 'A reason is required',
  'rtm.clearDecision': 'Delete the decision',
  'rtm.howVerified': 'How this matrix is verified',
  'rtm.howVerifiedBody':
    'pnpm check:docs audits every row: are the ids unique, does every referenced BR-… exist in {catalog}, does every code anchor resolve to a real file (and, if a line number is given, a real line), do the ids the backlog and release slices point at exist. When code moves, CI breaks — so the document cannot go stale in silence. This is an existence check: it proves the anchor resolves, not that the code behind it still does the right thing. That second question is answered by the semantic review above.',

  // ─── User flow ───────────────────────────────────────────────────────────
  // The document's own content (step text, rules, personas) comes from D1 and is
  // authored in one language by the product owner; only the panel's chrome
  // around it is translated here.
  'userFlow.loadFailed': 'Could not load the flow document',
  'userFlow.intro':
    'An end-to-end account of the flow from the moment a user signs up to graduation: which screen they see, which decision the system makes, how the first roadmap is built, how the daily practice loop works, and what happens if they do not graduate. Each phase has a diagram and a step-by-step table.',
  'userFlow.owner': 'Owner:',
  'userFlow.verifiedAt': 'Last verified:',
  'userFlow.staleAfter': '(threshold {days} days)',
  'userFlow.adr': 'Decision document:',
  'userFlow.ruleCatalog': 'Rule catalog:',
  'userFlow.currentDecision': 'Current product decision — {date}',
  'userFlow.readingGuide': '00 · How to read the diagrams',
  'userFlow.readingGuideHint':
    'The same actors appear in every diagram. The status label in the step tables says whether that step works today.',
  'userFlow.worksToday': 'works today',
  'userFlow.toBuild': 'to be built',
  'userFlow.brokenToday': 'behaves wrongly today, needs fixing',
  'userFlow.personas': '01 · Who arrives — 4 personas',
  'userFlow.personasHint':
    'Everyone goes through the same mandatory CV step. The difference: do they add job ads, how many, and how well does their CV evidence their real level?',
  'userFlow.day0': 'Day 0: {signal}',
  'userFlow.bigPicture': '02 · The big picture — end to end',
  'userFlow.bigPictureHint':
    'The whole journey at a glance before going into detail. The sections that follow open each box.',
  'userFlow.noOverview': 'No overview diagram is defined in this document.',
  'userFlow.phaseTitle': '{index} · {num} — {title}',
  'userFlow.colId': 'ID',
  'userFlow.colWho': 'Who',
  'userFlow.colWhat': 'What happens',
  'userFlow.colRuleNote': 'Rule / note',
  'userFlow.colTrace': 'Traceability',
  'userFlow.untraceable': 'untraceable',
  'userFlow.testCount': '✓ {count} tests',
  'userFlow.flowStep': 'The flow step',
  'userFlow.productRule': 'The product rule',
  'userFlow.rulesTitle': '{index} · Product rules',
  'userFlow.rulesHint': 'Rules that hold everywhere in the flow and are not up for debate.',
  'userFlow.colRule': 'Rule',
  'userFlow.colSays': 'What it says',
  'userFlow.colWhy': 'Why',
  'userFlow.colBusinessRule': 'Business rule',
  'userFlow.colCodeAnchor': 'Code anchor',
  'userFlow.notEnforced': 'not enforced',
  'userFlow.noAnchor': 'no anchor — cannot be reviewed',
  'userFlow.coverageTitle': '{index} · The coverage problem',
  'userFlow.coverageHint':
    "Today's subject catalog has 5 headings and is narrow. Making the CV mandatory will make that worse, because now everybody's real skills go through analysis.",
  'userFlow.inCatalogToday': 'In the catalog today',
  'userFlow.missingFromCatalog': 'Common in CVs and job ads but missing from the catalog:',
  'userFlow.approachTitle': 'The approach',
  'userFlow.approachBody':
    'Rather than growing the catalog by hand we read demand at its source: CVs and job ads already say which skills are needed.',
  'userFlow.coverageWarning': 'A direct consequence of making the CV mandatory',
  'userFlow.backlogTitle': '{index} · What is ready, what is to be built',
  'userFlow.backlogHint':
    'Which part of the flow works today and which part will be built alongside this decision.',
  'userFlow.colStep': 'Step',
  'userFlow.colNote': 'Note',
  'userFlow.slices': 'Release slices',
  'userFlow.slicesHint':
    'Each slice must produce an outcome for the user on its own — otherwise it is not a slice, it is a pile.',

  // ─── Users ───────────────────────────────────────────────────────────────
  'users.intro':
    'Every learner: profile · onboarding answers · assessment questions & answers · roadmaps. Click a user to inspect the full picture.',
  'users.infoTip':
    'Real learners only — simulation users (@sim.prepzio.local) are hidden. Opening a user shows their onboarding profile, every practice session with the questions and the answers they gave, and links to their generated roadmaps.',
  'users.colUser': 'User',
  'users.colPlan': 'Plan',
  'users.colPersona': 'Persona',
  'users.colRoadmaps': 'Roadmaps',
  'users.colJoined': 'Joined',
  'users.noOnboarding': 'no onboarding',
  'users.none': 'No users.',
  'users.allUsers': 'All users',
  'users.verified': 'verified',
  'users.joined': 'joined {date}',
  'users.llmTraces': 'LLM traces',
  'users.subscription': 'Subscription',
  'users.plan': 'Plan',
  'users.active': 'active',
  'users.inactiveFree': 'inactive (free)',
  'users.product': 'Product',
  'users.store': 'Store',
  'users.expires': 'Expires',
  'users.willRenew': 'Will renew',
  'users.onboardingProfile': 'Onboarding profile',
  'users.experience': 'Experience',
  'users.goal': 'Goal',
  'users.aiHabit': 'AI-usage habit',
  'users.completed': 'Completed',
  'users.weakAreasSelf': 'Weak areas (self-reported)',
  'users.aiTools': 'AI tools',
  'users.noOnboardingYet': 'This user has not completed onboarding.',
  'users.measuredWeak': 'Measured weak skills',
  'users.measuredWeakTip':
    "Skills whose running mastery — recency-weighted concept mastery across every graded assessment + practice answer (knowledge-tracing lite) — is below the roadmap's weak-skill threshold ({threshold}). This is the same signal the roadmap targets, unlike the self-reported onboarding weak areas above.",
  'users.noGraded':
    "No graded answers yet — not enough data to measure weak skills. (Weak areas above are the user's self-reported onboarding answers.)",
  'users.noneBelowThreshold':
    'No skills below the weak threshold ({threshold}) across {count} graded answers — no measured weakness.',
  'users.noneBelowThreshold_one':
    'No skills below the weak threshold ({threshold}) across {count} graded answer — no measured weakness.',
  'users.weakestFirst': 'Weakest first · mastery < {threshold} · from {count} graded answers.',
  'users.weakestFirst_one': 'Weakest first · mastery < {threshold} · from {count} graded answer.',
  'users.weakSkill': '{title} · {mastery}/100 · {count}×',
  'users.sessions': 'Sessions & answers ({count})',
  'users.noSessions': 'No practice or assessment sessions yet.',
  'users.sessionMeta': '{date} · {count} questions',
  'users.sessionMeta_one': '{date} · {count} question',
  'users.roadmaps': 'Roadmaps ({count})',
  'users.noRoadmaps': 'No roadmaps generated for this user yet.',
  'users.dangerZone': 'Danger zone',
  'users.dangerTip':
    'Deletes all learner data — onboarding profile, preferences, assessments, practice sessions, roadmaps, mastery/feedback, CV/JD docs, cost & event history. The sign-in is kept: the user can still log in but starts over as a fresh signup (re-onboards). Not reversible. Only accounts listed in the admin.resetAllowedEmails config can be reset — your own admin account included, once you add it there.',
  'users.dangerHint': "Reset all learner data but keep the account's sign-in (fresh-signup state).",
  'users.notAllowlisted':
    '{email} is not in the reset allowlist, so the API will refuse this (403).',
  'users.addToAllowlist':
    'Add it under Config → admin.resetAllowedEmails. The list is empty by default so that no real account can be wiped until somebody says which ones may be.',
  'users.resetButton': 'Reset all user data',
  'users.resetTitle': 'Reset all user data?',
  'users.resetBody':
    'This deletes every learner record for {email} (onboarding, sessions, roadmaps, mastery, feedback, docs, history). The sign-in is kept — they can log back in but land as a brand-new signup. This cannot be undone.',
  'users.typeEmail': "Type the user's email to confirm:",
  'users.resetConfirm': 'Reset data',

  // ─── Simulate ────────────────────────────────────────────────────────────
  'sim.intro':
    'Generate a draft roadmap for a synthetic persona end-to-end (onboarding → assessment with LLM-synthesized answers → real scoring → roadmap). It runs the exact production pipeline but on a throwaway user — no real learner is affected, and the result is flagged as a simulation.',
  'sim.infoTip':
    'The persona flows through the real pipeline: a synthetic user + onboarding profile → a baseline assessment whose answers the LLM writes in-character → the production scorer → the roadmap generator. Tick “Also complete the roadmap” to have the persona answer + get scored on every practice station too. Everything is namespaced to a throwaway @sim.prepzio.local user and deletable from here.',
  'sim.copyPersona': 'Copy persona from a user (optional)',
  'sim.blankPersona': '— Start from a blank persona —',
  'sim.loadingUsers': 'Loading users…',
  'sim.experience': 'Experience level',
  'sim.goal': 'Goal',
  'sim.aiHabit': 'AI-usage habit',
  'sim.weakAreas': 'Weak areas',
  'sim.questionCount': 'Assessment questions',
  'sim.label': 'Label (optional)',
  'sim.labelPlaceholder': 'e.g. junior · debugging gap',
  'sim.run': 'Run simulation',
  'sim.alsoComplete':
    'Also complete the roadmap (persona answers every practice station, scored end-to-end)',
  'sim.alsoCompleteHint':
    'Slower and uses more LLM calls (each station is answered + scored). Bank-first: stations whose skill has no published questions are skipped.',
  'sim.badge': '🧪 simulation',
  'sim.items': '{count} items',
  'sim.aiDep': 'AI-dep {score}',
  'sim.explanation': 'explanation {score}',
  'sim.concept': 'concept {score}',
  'sim.answered': '{count} answered',
  'sim.roadmapCompleted': '✓ roadmap completed',
  'sim.roadmapPartial': 'roadmap partial',
  'sim.stationsPractised': '{count} stations practised',
  'sim.practiceAnswers': '{count} practice answers',
  'sim.skippedNoBank': '{count} skipped (no bank)',
  'sim.hideSource': 'Hide source',
  'sim.showSource': 'Mermaid source',
  'sim.deleteSim': 'Delete simulation',
  'sim.costTitle': 'LLM cost of this run',
  'sim.costCalls': '{count} calls',
  'sim.costTokens': '{count} tokens',
  'sim.colPurpose': 'Purpose',
  'sim.colCalls': 'Calls',
  'sim.colTokens': 'Tokens',
  'sim.colCost': 'Cost',
  'sim.loadingRoadmap': 'Loading roadmap…',

  'sim.loopTitle': 'Graduation & re-loop simulation',
  'sim.loopTip':
    'Feeds synthetic per-skill mastery (below) into the real graduation evaluator (BR-72) and, on a re-loop, the real rule-based roadmap generator (BR-56). Mastery starts at base and rises by +/loop each loop, so it eventually clears the threshold. Each step shows which rule fired.',
  'sim.baseMastery': 'Base mastery',
  'sim.perLoop': '+ per loop',
  'sim.maxLoops': 'Max loops',
  'sim.simulateLoop': 'Simulate loop',
  'sim.loops': '{count} loops',
  'sim.loops_one': '{count} loop',
  'sim.graduatedAt': ' · graduated at loop {loop}',
  'sim.loopStep': 'Loop {loop} · {outcome}',
  'sim.nextOptions': 'Next options — level up: {levelUp} · new track: {tracks}',
  'sim.outcome.graduated': 'graduated',
  'sim.outcome.reloop': 're-loop',
  'sim.outcome.stalled': 'stalled',

  // ─── Costs ───────────────────────────────────────────────────────────────
  'costs.intro':
    'Live LLM spend from the llm_calls ledger — every tutor, scoring, authoring, roadmap and CV/JD call. Nothing here is hardcoded.',
  'costs.infoTip':
    'Every model call writes one ledger row (purpose · provider · model · tokens · real USD cost). All-time is the whole ledger; Last 30d and Month to date filter by the call date. The budget cap governs only the authoring/generation tier, but month-to-date shown is total spend across every purpose.',
  'costs.allTime': 'All-time',
  'costs.lastDays': 'Last {days}d',
  'costs.monthToDate': 'Month to date',
  'costs.tokensAllTime': 'Tokens (all-time)',
  'costs.callsSub': '{count} calls',
  'costs.tokensSub': '{in} in · {out} out',
  'costs.budget': 'Authoring monthly budget',
  'costs.budgetNote':
    'The cap governs the authoring/generation tier; month-to-date shown is total spend across all purposes.',
  'costs.byArea': 'Spend by area',
  'costs.byAreaTip':
    'Learner-facing spend grows with the number of people using the product — onboarding, practice and planning. Ours does not: content authoring is paid once per question on an admin click, and internal tooling is our own instruments pointed at our own repository, with no learner waiting on them.',
  'costs.learnerFacing': 'Learner-facing',
  'costs.learnerFacingSub': '{share} of the window · scales with users',
  'costs.ours': 'Ours (content + tooling)',
  'costs.oursSub': '{share} of the window · scales with our own activity',
  'costs.colArea': 'Area',
  'costs.colCost': 'Cost',
  'costs.colShare': 'Share',
  'costs.colCalls': 'Calls',
  'costs.colPurposes': 'Purposes',
  'costs.noSpend': 'No spend in this window.',
  'costs.byPurpose': 'Spend by purpose',
  'costs.colPurpose': 'Purpose',
  'costs.colInTokens': 'In tokens',
  'costs.colOutTokens': 'Out tokens',
  'costs.noCalls': 'No LLM calls recorded yet.',
  'costs.byModel': 'Spend by model',
  'costs.colModel': 'Model',
  'costs.byStatus': 'Calls by status',
  'costs.statusLine': '{count} calls · {cost}',
  'costs.daily': 'Daily spend (last {days}d)',
  'costs.dayTooltip': '{day}: {cost} · {calls} calls',
  'costs.recent': 'Recent calls',
  'costs.recentTip':
    'Newest 50 ledger rows. LLM trace opens Datadog LLM Observability for that span. Rows ingested before span ids were stored have no link.',
  'costs.colWhen': 'When',
  'costs.colLatency': 'Latency',
  'costs.colTrace': 'Trace',
  'costs.llmTrace': 'LLM trace',
  'costs.noPurposeDescription':
    'No description defined for this purpose — add it to LLM_PURPOSE_META (@prepzio/shared).',
  'costs.status.ok': 'ok',
  'costs.status.error': 'error',
  'costs.status.rate_limited': 'rate limited',

  // ─── Emails ──────────────────────────────────────────────────────────────
  'email.intro':
    'Send a plain message or a published Resend template. Delivery updates arrive via signed webhooks.',
  'email.compose': 'Compose',
  'email.composeMode': 'Compose mode',
  'email.plain': 'Plain text',
  'email.template': 'Template',
  'email.recipient': 'Recipient',
  'email.publishedTemplate': 'Published template',
  'email.selectTemplate': 'Select a template…',
  'email.noTemplates': 'No published templates loaded (Full Access API key needed).',
  'email.subject': 'Subject',
  'email.subjectPlaceholder': 'Email subject',
  'email.message': 'Message',
  'email.messagePlaceholder': 'Write the email body…',
  'email.appendHtml': 'Append HTML below the message',
  'email.htmlLabel': 'HTML (appended after the text message)',
  'email.loadingVariables': 'Loading template variables…',
  'email.templateMeta': 'Subject: {subject} · From: {from}',
  'email.fromDefault': 'EMAIL_FROM default',
  'email.send': 'Send email',
  'email.newKey': 'New key',
  'email.fillRequired': 'Fill required variables: {keys}',
  'email.mustBeNumber': 'Variable {key} must be a number',
  'email.subjectAndTextRequired': 'Subject and text body are required',
  'email.confirmTemplate': 'Send template “{name}” to {to}?',
  'email.confirmPlain': 'Send plain email to {to}?',
  'email.replayed': 'Replayed existing send {id} ({status})',
  'email.sent': 'Sent — id {id} ({status})',
  'email.history': 'Delivery history',
  'email.filterByEmail': 'Filter by email…',
  'email.loadingHistory': 'Loading history…',
  'email.noSends': 'No sends yet.',
  'email.plainLabel': 'Plain text',
  'email.resendId': 'Resend: {id}',
  'email.loadMore': 'Load more',
  'email.status.queued': 'queued',
  'email.status.sent': 'sent',
  'email.status.delivered': 'delivered',
  'email.status.delivery_delayed': 'delayed',
  'email.status.bounced': 'bounced',
  'email.status.complained': 'complained',
  'email.status.failed': 'failed',
  'email.status.suppressed': 'suppressed',

  // ─── System health ───────────────────────────────────────────────────────
  'health.intro':
    'Last {hours}h. LLM numbers come from the llm_calls ledger; monitors, trends, errors and job runs come from Datadog (env {env}).',
  'health.infoTip':
    'Telemetry env tag is {env} (DD_ENV). Datadog is queried by the API with a read Application key — the panel never calls Datadog itself. This screen answers the day-to-day questions; digging into a single trace or searching logs freely still belongs in Datadog, and every row here deep-links there.',
  'health.llmCalls': 'LLM calls',
  'health.ledgerWindow': 'ledger · {hours}h',
  'health.llmErrors': 'LLM errors',
  'health.llmErrorsSub': 'error + rate_limited',
  'health.fallbackRate': 'Fallback rate',
  'health.fallbackSub': '{fallbacks} of {calls}',
  'health.llmP95': 'LLM p95 latency',
  'health.fromLedger': 'from llm_calls',
  'health.httpP95': 'HTTP p95',
  'health.apm': 'Datadog APM',
  'health.llmSpend': 'LLM spend',
  'health.llmObs': 'Datadog LLM Obs',
  'health.openErrors': 'Open errors',
  'health.errorTracking': 'Error Tracking',
  'health.notConfigured': 'Datadog read is not configured',
  'health.notConfiguredBody':
    'Bind DATADOG_APP_KEY (Application key) on the worker. DATADOG_API_KEY alone only writes traces.',
  'health.queryFailed': 'Datadog query failed',
  'health.monitors': 'Monitors',
  'health.firing': '{count} firing',
  'health.noMonitors': 'No monitors tagged team:prepzio.',
  'health.colMonitor': 'Monitor',
  'health.colState': 'State',
  'health.colChanged': 'Changed',
  'health.requests': 'Requests',
  'health.errors': 'Errors',
  'health.hourlyWindow': 'hourly · last {hours}h',
  'health.recentErrors': 'Recent errors',
  'health.noErrorLogs': 'No error logs in this window.',
  'health.colWhen': 'When',
  'health.colRoute': 'Route',
  'health.colCode': 'Code',
  'health.colMessage': 'Message',
  'health.colTrace': 'Trace',
  'health.openTrace': 'open',
  'health.jobs': 'Crons & queues',
  'health.noJobLogs': 'No job logs in this window.',
  'health.colJob': 'Job',
  'health.colKind': 'Kind',
  'health.colLastRun': 'Last run',
  'health.colRuns': 'Runs',
  'health.colErrors': 'Errors',
  'health.noIssues': 'No open Error Tracking issues in this window.',
  'health.colError': 'Error',
  'health.colEvents': 'Events',
  'health.colLastSeen': 'Last seen',
  'health.justNow': 'just now',
  'health.minutesAgo': '{n}m ago',
  'health.hoursAgo': '{n}h ago',
  'health.daysAgo': '{n}d ago',

  'skillQueue.acceptNote':
    'Accept: the row is correct, but it is not a taxonomy entry we would write (an internal tool, a niche library). It leaves the queue and stays user_generated — “we did not write this” is true of the row and a real signal to whoever reviews the taxonomy later.',
} as const;

export type MessageKey = keyof typeof en;
